import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@medikei/ui';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import {
  Search,
  Eye,
  ChevronLeft,
  ChevronRight,
  User,
  Calendar,
  Heart,
  Activity,
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  AlertCircle,
  X,
  FileText,
  Pill,
  Award,
  Stethoscope,
  Clock,
} from 'lucide-react';
import { httpClient } from '@/api';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';

interface Patient {
  id: string;
  name: string;
  email: string;
  phone?: string;
  cpf: string;
  birthDate: string;
  gender: string;
  city: string;
  state: string;
  totalAppointments: number;
  totalSpent: number;
  lastAppointment?: string;
  registeredAt: string;
  healthConditions: string[];
  allergies: string[];
  isActive: boolean;
  prescriptionsCount?: number;
  examsCount?: number;
  certificatesCount?: number;
  doctorsConsulted?: Array<{ id: string; name: string; specialty?: string; crm?: string | null }>;
  recentAppointments?: Array<{
    id: string;
    date: string;
    status: string;
    type: string;
    serviceType?: string;
    price: number;
    doctorName?: string;
    specialty?: string;
  }>;
}

interface PatientsResponse {
  patients: Patient[];
  total: number;
  page: number;
  pageSize: number;
  stats: {
    totalPatients: number;
    activePatients: number;
    newThisMonth: number;
    totalRevenue: number;
  };
}

export function AdminPatientsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<PatientsResponse>({
    queryKey: ['admin', 'patients', page, filter, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: '10',
      });
      if (search) params.append('search', search);
      if (filter !== 'ALL') params.append('status', filter === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE');

      const json = await httpClient.get<{ data: any[]; meta: { page: number; perPage: number; total: number } }>(`/admin/patients?${params}`);
      const rawList = Array.isArray(json?.data) ? json.data : [];
      const totalCount = json?.meta?.total ?? rawList.length;
      const activeCount = rawList.filter((p: any) => p.isActive ?? true).length;
      const now = new Date();
      const newThisMonthCount = rawList.filter((p: any) => {
        if (!p.createdAt) return false;
        const d = new Date(p.createdAt);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length;
      const totalRevenueSum = rawList.reduce((acc: number, p: any) => acc + (Number(p.totalSpent) || 0), 0);

      return {
        patients: rawList.map((p: any) => ({
          id: p.id,
          name: p.name || 'Paciente',
          email: p.email || 'Não informado',
          phone: p.phone && p.phone !== 'N/A' ? p.phone : undefined,
          cpf: p.cpf && p.cpf !== 'N/A' ? p.cpf : '',
          birthDate: p.birthDate && p.birthDate !== 'N/A' ? p.birthDate : '',
          gender: p.gender || '',
          city: p.city && p.city !== 'N/A' ? p.city : '',
          state: p.state && p.state !== 'N/A' ? p.state : '',
          totalAppointments: Number(p.totalAppointments) || 0,
          totalSpent: Number(p.totalSpent) || 0,
          lastAppointment: p.lastAppointment,
          registeredAt: p.createdAt,
          healthConditions: Array.isArray(p.healthConditions) ? p.healthConditions : [],
          allergies: Array.isArray(p.allergies) ? p.allergies : [],
          isActive: p.isActive ?? true,
          prescriptionsCount: Number(p.prescriptionsCount) || 0,
          examsCount: Number(p.examsCount) || 0,
          certificatesCount: Number(p.certificatesCount) || 0,
          doctorsConsulted: Array.isArray(p.doctorsConsulted) ? p.doctorsConsulted : [],
          recentAppointments: Array.isArray(p.recentAppointments) ? p.recentAppointments : [],
        })),
        total: totalCount,
        page: json?.meta?.page ?? page,
        pageSize: json?.meta?.perPage ?? 10,
        stats: {
          totalPatients: totalCount,
          activePatients: activeCount,
          newThisMonth: newThisMonthCount,
          totalRevenue: totalRevenueSum,
        },
      };
    },
  });

  const filteredPatients = data?.patients.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
                          p.email.toLowerCase().includes(search.toLowerCase()) ||
                          p.cpf.includes(search);
    const matchesFilter = filter === 'ALL' ||
                           (filter === 'ACTIVE' && p.isActive) ||
                           (filter === 'INACTIVE' && !p.isActive);
    return matchesSearch && matchesFilter;
  }) || [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr || dateStr === 'N/A') return 'Nenhuma';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Nenhuma';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d);
  };

  const calculateAge = (birthDate?: string | null) => {
    if (!birthDate || birthDate === 'N/A') return '—';
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime())) return '—';
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 0 && age < 130 ? `${age} anos` : '—';
  };

  const getInitials = (name?: string) => {
    if (!name || name === 'N/A') return 'P';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const firstPart = parts[0];
    const lastPart = parts[parts.length - 1];
    if (parts.length >= 2 && firstPart && lastPart) {
      const first = firstPart[0] || '';
      const last = lastPart[0] || '';
      return (first + last).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase() || 'P';
  };

  const formatLocation = (city?: string, state?: string) => {
    const c = city && city !== 'N/A' && city.trim() !== '' ? city.trim() : null;
    const s = state && state !== 'N/A' && state.trim() !== '' ? state.trim() : null;
    if (c && s) return `${c} - ${s}`;
    if (c) return c;
    if (s) return s;
    return 'Não informada';
  };

  const formatMaskedCpf = (cpf?: string) => {
    if (!cpf || cpf === 'N/A' || cpf.trim() === '') return 'Não informado';
    const clean = cpf.replace(/\D/g, '');
    if (clean.length === 11) {
      return `***.***.${clean.slice(6, 9)}-${clean.slice(9, 11)}`;
    }
    return cpf;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Gerenciamento de Pacientes</h1>
      </div>

      {/* 1. KPIs Cards com design moderno sem duplo padding e com métricas reais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total de Pacientes */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/60 flex items-center justify-center shrink-0">
            <User className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total de Pacientes</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{data?.stats.totalPatients.toLocaleString('pt-BR') ?? 0}</p>
          </div>
        </div>

        {/* Pacientes Ativos */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/60 flex items-center justify-center shrink-0">
            <Activity className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pacientes Ativos</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{data?.stats.activePatients.toLocaleString('pt-BR') ?? 0}</p>
          </div>
        </div>

        {/* Novos no Mês */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 border border-purple-100 dark:border-purple-900/60 flex items-center justify-center shrink-0">
            <Calendar className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Novos (Mês)</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{data?.stats.newThisMonth ?? 0}</p>
          </div>
        </div>

        {/* Receita Total */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900/60 flex items-center justify-center shrink-0">
            <Heart className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Receita Total</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{formatCurrency(data?.stats.totalRevenue || 0)}</p>
          </div>
        </div>
      </div>

      {/* 2. Barra de Busca e Filtro de Status Estilizados */}
      <div className="flex items-center flex-wrap gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nome, email ou CPF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-xs transition-all"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="px-4 py-2.5 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-xs transition-all cursor-pointer"
        >
          <option value="ALL">Todos os Status</option>
          <option value="ACTIVE">Apenas Ativos</option>
          <option value="INACTIVE">Apenas Inativos</option>
        </select>
      </div>

      {isLoading ? (
        <LoadingOverlay message="Carregando pacientes..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar pacientes" onRetry={() => refetch()} />
      ) : filteredPatients.length === 0 ? (
        <EmptyState
          icon="User"
          title="Nenhum paciente encontrado"
          description={search || filter !== 'ALL' ? 'Tente ajustar os filtros de busca' : 'Nenhum paciente cadastrado ainda'}
          action={{ label: 'Atualizar', onClick: () => refetch() }}
        />
      ) : (
        <div className="rounded-2xl border border-slate-200/90 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/80 dark:bg-slate-800/60 border-b border-slate-200/80 dark:border-neutral-800">
                <tr>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Paciente</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Idade</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Localização</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Consultas</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total Gasto</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Última Consulta</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-neutral-800/70 text-sm">
                {filteredPatients.map((patient) => {
                  const ageFormatted = calculateAge(patient.birthDate);
                  const locationFormatted = formatLocation(patient.city, patient.state);
                  return (
                    <tr key={patient.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors duration-150">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 via-primary to-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-xs shrink-0 tracking-wider">
                            {getInitials(patient.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-foreground text-sm truncate">{patient.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{patient.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-slate-600 dark:text-slate-300 font-medium">
                        {ageFormatted !== '—' ? (
                          <span>{ageFormatted}</span>
                        ) : (
                          <span className="text-muted-foreground/60 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-slate-600 dark:text-slate-300">
                        {locationFormatted !== 'Não informada' ? (
                          <span className="font-medium text-slate-700 dark:text-slate-200">{locationFormatted}</span>
                        ) : (
                          <span className="text-muted-foreground/60 text-xs">Não informada</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-center font-bold text-foreground">
                        {patient.totalAppointments}
                      </td>
                      <td className="px-4 py-3.5 font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(patient.totalSpent)}
                      </td>
                      <td className="px-4 py-3.5 text-muted-foreground text-xs">
                        {formatDate(patient.lastAppointment)}
                      </td>
                      <td className="px-4 py-3.5">
                        {patient.isActive ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Ativo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            Inativo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <button
                          onClick={() => setSelectedPatient(patient)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900/60 border border-blue-200/60 dark:border-blue-800 transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-2xs"
                          title="Visualizar detalhes do paciente"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>Detalhes</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200/80 dark:border-neutral-800 bg-slate-50/50 dark:bg-slate-900/30">
            <p className="text-xs font-medium text-muted-foreground">
              Mostrando {((page - 1) * 10) + 1} a {Math.min(page * 10, data?.total || 0)} de {data?.total || 0} pacientes
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-2 border border-slate-200 dark:border-neutral-700 rounded-xl hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-2xs cursor-pointer"
                title="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3.5 py-1 bg-primary text-white text-xs font-bold rounded-xl shadow-xs">
                {page}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page * 10 >= (data?.total || 0)}
                className="p-2 border border-slate-200 dark:border-neutral-700 rounded-xl hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-2xs cursor-pointer"
                title="Próxima página"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Modal de Detalhes do Paciente (UX Premium) */}
      <Dialog open={!!selectedPatient} onOpenChange={() => setSelectedPatient(null)}>
        <DialogContent className="max-w-lg w-[95vw] sm:w-full p-0 overflow-hidden rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-neutral-800 shadow-2xl flex flex-col max-h-[90vh]">
          {/* Header fixo */}
          <div className="px-4 sm:px-5 py-3.5 sm:py-4 bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <ShieldCheck className="w-5 h-5 text-blue-200 shrink-0" />
              <DialogTitle className="text-sm sm:text-base font-bold text-white truncate">
                Ficha Cadastral do Paciente
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={() => setSelectedPatient(null)}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer shrink-0"
              title="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {selectedPatient && (
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto flex-1 overscroll-contain">
              {/* Header do Perfil */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-3.5 sm:pb-4 border-b border-slate-100 dark:border-neutral-800">
                <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-tr from-blue-600 via-primary to-indigo-600 text-white font-black text-base sm:text-lg flex items-center justify-center shadow-md shrink-0">
                    {getInitials(selectedPatient.name)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-foreground truncate">{selectedPatient.name}</h3>
                    <a
                      href={`mailto:${selectedPatient.email}`}
                      className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5 truncate"
                    >
                      <Mail className="w-3 h-3 shrink-0" />
                      <span className="truncate">{selectedPatient.email}</span>
                    </a>
                  </div>
                </div>

                <div className="shrink-0">
                  {selectedPatient.isActive ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Ativo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                      Inativo
                    </span>
                  )}
                </div>
              </div>

              {/* Grid de Informações Estruturadas */}
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3 text-xs">
                {/* CPF */}
                <div className="p-3 sm:p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-0.5 sm:space-y-1 min-w-0">
                  <span className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-wider block truncate">CPF</span>
                  <p className="font-semibold text-foreground text-xs sm:text-sm truncate">{formatMaskedCpf(selectedPatient.cpf)}</p>
                </div>

                {/* Telefone */}
                <div className="p-3 sm:p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-0.5 sm:space-y-1 min-w-0">
                  <span className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-wider block flex items-center gap-1 truncate">
                    <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                    <span className="truncate">Telefone</span>
                  </span>
                  <p className="font-semibold text-foreground text-xs sm:text-sm truncate">
                    {selectedPatient.phone && selectedPatient.phone !== 'N/A' ? (
                      <a href={`tel:${selectedPatient.phone}`} className="text-primary hover:underline truncate">
                        {selectedPatient.phone}
                      </a>
                    ) : (
                      <span className="text-muted-foreground/60 font-normal">Não informado</span>
                    )}
                  </p>
                </div>

                {/* Idade / Nascimento */}
                <div className="p-3 sm:p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-0.5 sm:space-y-1 min-w-0">
                  <span className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-wider block flex items-center gap-1 truncate">
                    <Calendar className="w-3 h-3 text-slate-400 shrink-0" />
                    <span className="truncate">Idade / Nasc.</span>
                  </span>
                  <p className="font-semibold text-foreground text-xs sm:text-sm truncate">
                    {calculateAge(selectedPatient.birthDate) !== '—' ? calculateAge(selectedPatient.birthDate) : 'Não informada'}
                  </p>
                </div>

                {/* Localização */}
                <div className="p-3 sm:p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-0.5 sm:space-y-1 min-w-0">
                  <span className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-wider block flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                    <span className="truncate">Localização</span>
                  </span>
                  <p className="font-semibold text-foreground text-xs sm:text-sm truncate">
                    {formatLocation(selectedPatient.city, selectedPatient.state)}
                  </p>
                </div>
              </div>

              {/* Resumo Clínico em Números: Consultas, Receitas, Exames, Atestados */}
              <div className="grid grid-cols-4 gap-1.5 sm:gap-2 pt-1 text-center">
                <div className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-blue-50/80 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-900/40">
                  <Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 mx-auto mb-0.5 sm:mb-1" />
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold text-blue-700 dark:text-blue-300 block truncate">Consultas</span>
                  <span className="text-sm sm:text-base font-extrabold text-foreground">{selectedPatient.totalAppointments}</span>
                </div>
                <div className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-emerald-50/80 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-900/40">
                  <Pill className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 mx-auto mb-0.5 sm:mb-1" />
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-300 block truncate">Receitas</span>
                  <span className="text-sm sm:text-base font-extrabold text-foreground">{selectedPatient.prescriptionsCount ?? 0}</span>
                </div>
                <div className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-purple-50/80 dark:bg-purple-950/40 border border-purple-200/60 dark:border-purple-900/40">
                  <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 mx-auto mb-0.5 sm:mb-1" />
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold text-purple-700 dark:text-purple-300 block truncate">Exames</span>
                  <span className="text-sm sm:text-base font-extrabold text-foreground">{selectedPatient.examsCount ?? 0}</span>
                </div>
                <div className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-amber-50/80 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-900/40">
                  <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600 mx-auto mb-0.5 sm:mb-1" />
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold text-amber-700 dark:text-amber-300 block truncate">Atestados</span>
                  <span className="text-sm sm:text-base font-extrabold text-foreground">{selectedPatient.certificatesCount ?? 0}</span>
                </div>
              </div>

              {/* Médicos com quem o paciente já consultou */}
              <div className="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-2">
                <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Stethoscope className="w-3.5 h-3.5 text-blue-600" />
                  <span>Médicos que Atenderam ({selectedPatient.doctorsConsulted?.length || 0})</span>
                </span>
                {selectedPatient.doctorsConsulted && selectedPatient.doctorsConsulted.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {selectedPatient.doctorsConsulted.map((doc) => (
                      <div key={doc.id} className="p-2.5 rounded-xl bg-white dark:bg-neutral-900 border border-slate-200/80 dark:border-neutral-700 flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 font-bold text-xs flex items-center justify-center shrink-0">
                          MD
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-xs text-foreground truncate">{doc.name}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{doc.specialty} {doc.crm ? `• CRM ${doc.crm}` : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-xs italic">Nenhum atendimento realizado até o momento.</p>
                )}
              </div>

              {/* Histórico Recente de Consultas */}
              {selectedPatient.recentAppointments && selectedPatient.recentAppointments.length > 0 && (
                <div className="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-2">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    <span>Últimas Consultas Registradas</span>
                  </span>
                  <div className="space-y-1.5 pt-0.5 max-h-48 overflow-y-auto pr-1">
                    {selectedPatient.recentAppointments.map((appt) => (
                      <div key={appt.id} className="p-2.5 rounded-xl bg-white dark:bg-neutral-900 border border-slate-200/80 dark:border-neutral-700 flex items-center justify-between text-xs gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-foreground truncate">
                            {appt.doctorName || 'Médico'} <span className="text-[10px] font-normal text-muted-foreground">({appt.specialty || 'Clínica Geral'})</span>
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                            {new Date(appt.date).toLocaleDateString('pt-BR')} às {new Date(appt.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} • {appt.serviceType || 'TELEMEDICINA'}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="font-extrabold text-foreground">{formatCurrency(appt.price)}</span>
                          <span className="block text-[9px] uppercase font-bold text-emerald-600 dark:text-emerald-400">{appt.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Alergias & Condições Preexistentes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 text-xs">
                {/* Alergias */}
                <div className="p-3 sm:p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1.5">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-amber-500 shrink-0" />
                    <span>Alergias Medicamentosas</span>
                  </span>
                  {selectedPatient.allergies.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {selectedPatient.allergies.map((allergy, i) => (
                        <span key={i} className="px-2.5 py-0.5 rounded-md bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 border border-red-200 text-xs font-semibold">
                          {allergy}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground font-medium text-xs">Nenhuma alergia relatada pelo paciente.</p>
                  )}
                </div>

                {/* Condições de Saúde */}
                <div className="p-3 sm:p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1.5">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <Heart className="w-3 h-3 text-rose-500 shrink-0" />
                    <span>Condições Preexistentes</span>
                  </span>
                  {selectedPatient.healthConditions.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {selectedPatient.healthConditions.map((cond, i) => (
                        <span key={i} className="px-2.5 py-0.5 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 text-xs font-semibold">
                          {cond}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground font-medium text-xs">Nenhuma condição informada.</p>
                  )}
                </div>
              </div>

              {/* Botão de Fechar */}
              <div className="pt-2 flex justify-end shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setSelectedPatient(null)}
                  className="rounded-xl px-5 text-xs font-semibold cursor-pointer w-full sm:w-auto h-10"
                >
                  Fechar Ficha
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
